import React, { createContext, useContext, useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import { useAuth, getAuthHeaders } from './AuthContext';
import { API_BASE } from '../config/api';

/* ────────────────────────────────────────────────────────────────────────────
 * Persistent Workspace + Film selection (Phase 1 — state plumbing only)
 *
 * Storage keys are namespaced under `frameflow.*` so they don't collide
 * with other app state. The legacy key `currentWorkspaceId` is migrated
 * on first read for users who saved it under the old name.
 *
 * Reads are wrapped in try/catch because localStorage can throw in
 * private-mode / sandboxed iframes / SSR.
 * ──────────────────────────────────────────────────────────────────────── */
export const LS_WORKSPACE_KEY = 'frameflow.currentWorkspaceId';
export const LS_WORKSPACE_KEY_LEGACY = 'currentWorkspaceId';
export const LS_FILM_KEY = 'frameflow.currentFilmId';

function readPersistedWorkspaceId() {
  try {
    let id = localStorage.getItem(LS_WORKSPACE_KEY);
    if (!id) {
      const legacy = localStorage.getItem(LS_WORKSPACE_KEY_LEGACY);
      if (legacy) {
        localStorage.setItem(LS_WORKSPACE_KEY, legacy);
        id = legacy;
      }
    }
    return id || null;
  } catch {
    return null;
  }
}

function writePersistedWorkspaceId(id) {
  try {
    if (id) localStorage.setItem(LS_WORKSPACE_KEY, id);
    else localStorage.removeItem(LS_WORKSPACE_KEY);
  } catch {
    /* ignore storage errors */
  }
}

function clearPersistedFilmId() {
  try {
    localStorage.removeItem(LS_FILM_KEY);
  } catch {
    /* ignore storage errors */
  }
}

const WorkspaceContext = createContext(null);

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error('useWorkspace must be used within a WorkspaceProvider');
  }
  return context;
};

export const WorkspaceProvider = ({ children }) => {
  const { user, token } = useAuth();
  const [workspaces, setWorkspaces] = useState([]);
  // Hydrate persisted workspace id BEFORE first render to avoid a one-tick
  // flicker where consumers see `currentWorkspaceId === null` and route to
  // an account-scoped URL only to swap a moment later.
  const [currentWorkspaceId, setCurrentWorkspaceIdState] = useState(() => readPersistedWorkspaceId());
  const [currentWorkspace, setCurrentWorkspace] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Track previous workspace id so cross-context effects (FilmContext) can
  // distinguish "first mount" from a real switch via ref-equality.
  const currentWorkspaceIdRef = useRef(currentWorkspaceId);
  useEffect(() => {
    currentWorkspaceIdRef.current = currentWorkspaceId;
  }, [currentWorkspaceId]);

  // Upgrade modal state
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeTrigger, setUpgradeTrigger] = useState(null);

  const userId = user?.id;

  // Load workspaces when user is authenticated
  const loadWorkspaces = useCallback(async () => {
    if (!userId) {
      setWorkspaces([]);
      setCurrentWorkspace(null);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_BASE}/workspaces`, { headers: getAuthHeaders() });
      const workspaceList = response.data || [];
      setWorkspaces(workspaceList);
      // NOTE: auto-select / validation moved into a dedicated effect below
      // so it can react to *any* change in (workspaces, currentWorkspaceId)
      // instead of being inline-coupled to the fetch result.
    } catch (err) {
      console.error('Failed to load workspaces:', err);
      setError(err.response?.data?.detail || 'Failed to load workspaces');
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadWorkspaces();
  }, [loadWorkspaces]);

  /* ── Auto-select & validate persisted workspace ─────────────────────────
   * Runs whenever the workspaces list or the persisted id changes.
   *
   *   1. If a persisted currentWorkspaceId is set but no longer present in
   *      the user's workspace list (deleted, lost access) — clear both
   *      workspace and film state.
   *   2. If no workspace is currently selected and the user has exactly
   *      one workspace — auto-select it. (Zero workspaces is left as
   *      null per spec; we do not auto-pick when there are 2+.)
   * ─────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (loading) return;
    if (!userId) return;

    if (currentWorkspaceId) {
      if (workspaces.length > 0 && !workspaces.some((w) => w.id === currentWorkspaceId)) {
        // Persisted ws no longer exists / lost access → clear both.
        setCurrentWorkspaceIdState(null);
        writePersistedWorkspaceId(null);
        clearPersistedFilmId();
      }
      return;
    }

    if (workspaces.length === 1) {
      const onlyWs = workspaces[0];
      setCurrentWorkspaceIdState(onlyWs.id);
      writePersistedWorkspaceId(onlyWs.id);
    }
  }, [loading, userId, workspaces, currentWorkspaceId]);

  /* ── Sync the resolved `currentWorkspace` object from the id ──────────
   * `currentWorkspaceId` is the source of truth; the object is derived
   * once the workspaces list contains a matching entry.
   * ─────────────────────────────────────────────────────────────────── */
  useEffect(() => {
    if (!currentWorkspaceId) {
      if (currentWorkspace !== null) setCurrentWorkspace(null);
      return;
    }
    const ws = workspaces.find((w) => w.id === currentWorkspaceId);
    if (ws && ws !== currentWorkspace) {
      setCurrentWorkspace(ws);
    }
    // If the list hasn't loaded yet, leave currentWorkspace as-is; the
    // next render after fetch will resolve it.
  }, [currentWorkspaceId, workspaces, currentWorkspace]);

  /* ── ID-based setter (the new public API) ────────────────────────────
   * Phase 1 callers (RouteContextBridge, future top-nav) use this. The
   * legacy `selectWorkspace(workspace)` is preserved as a thin wrapper
   * that delegates here so existing components keep working.
   *
   * Switch semantics:
   *   - if newId === currentWorkspaceId → no-op (no film reset).
   *   - if it differs → persist the new id AND clear the persisted film
   *     id from localStorage. The live `currentFilmId` state lives in
   *     FilmContext; FilmContext watches `currentWorkspaceId` via the
   *     context value and clears its own state on a real switch.
   * ─────────────────────────────────────────────────────────────────── */
  const setCurrentWorkspaceId = useCallback((newId) => {
    setCurrentWorkspaceIdState((prev) => {
      const next = newId || null;
      if (prev === next) return prev;
      writePersistedWorkspaceId(next);
      clearPersistedFilmId();
      return next;
    });
  }, []);

  // Select a workspace
  const selectWorkspace = useCallback((workspace) => {
    setCurrentWorkspaceId(workspace?.id || null);
  }, [setCurrentWorkspaceId]);

  // Check if user can create a new workspace
  const canCreateWorkspace = useCallback(async () => {
    try {
      const response = await axios.get(`${API_BASE}/workspaces/can-create`, { headers: getAuthHeaders() });
      return response.data;
    } catch (err) {
      console.error('Failed to check workspace creation:', err);
      return {
        can_create: false,
        reason: 'Unable to check workspace creation status',
        upgrade_required: false
      };
    }
  }, []);

  // Create a new workspace
  const createWorkspace = useCallback(async (name, description) => {
    try {
      const response = await axios.post(`${API_BASE}/workspaces`, { name, description }, { headers: getAuthHeaders() });
      const newWorkspace = response.data;
      setWorkspaces(prev => [...prev, newWorkspace]);
      return newWorkspace;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to create workspace');
    }
  }, []);

  // Update workspace
  const updateWorkspace = useCallback(async (workspaceId, data) => {
    try {
      const response = await axios.put(`${API_BASE}/workspaces/${workspaceId}`, data, { headers: getAuthHeaders() });
      const updatedWorkspace = response.data;
      setWorkspaces(prev => prev.map(w => w.id === workspaceId ? updatedWorkspace : w));
      if (currentWorkspace?.id === workspaceId) {
        setCurrentWorkspace(updatedWorkspace);
      }
      return updatedWorkspace;
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to update workspace');
    }
  }, [currentWorkspace]);

  // Delete workspace
  const deleteWorkspace = useCallback(async (workspaceId) => {
    try {
      await axios.delete(`${API_BASE}/workspaces/${workspaceId}`, { headers: getAuthHeaders() });
      setWorkspaces(prev => prev.filter(w => w.id !== workspaceId));
      if (currentWorkspaceIdRef.current === workspaceId) {
        // Use the id-based setter so the new persistence key & film
        // reset semantics are applied uniformly.
        setCurrentWorkspaceId(null);
      }
    } catch (err) {
      throw new Error(err.response?.data?.detail || 'Failed to delete workspace');
    }
  }, [setCurrentWorkspaceId]);

  // Get workspace features
  const getWorkspaceFeatures = useCallback(async (workspaceId) => {
    try {
      const response = await axios.get(`${API_BASE}/workspaces/${workspaceId}/features`, { headers: getAuthHeaders() });
      return response.data;
    } catch (err) {
      console.error('Failed to load workspace features:', err);
      return null;
    }
  }, []);

  // Get workspace usage
  const getWorkspaceUsage = useCallback(async (workspaceId) => {
    try {
      const response = await axios.get(`${API_BASE}/workspaces/${workspaceId}/usage`, { headers: getAuthHeaders() });
      return response.data;
    } catch (err) {
      console.error('Failed to load workspace usage:', err);
      return null;
    }
  }, []);

  // Get workspace upgrade status
  const getWorkspaceUpgradeStatus = useCallback(async (workspaceId) => {
    try {
      const response = await axios.get(`${API_BASE}/workspaces/${workspaceId}/upgrade/status`, { headers: getAuthHeaders() });
      return response.data;
    } catch (err) {
      console.error('Failed to load upgrade status:', err);
      return null;
    }
  }, []);

  // Open upgrade modal for current workspace
  const openUpgradeModal = useCallback((trigger = null) => {
    setUpgradeTrigger(trigger);
    setUpgradeModalOpen(true);
  }, []);

  // Close upgrade modal
  const closeUpgradeModal = useCallback(() => {
    setUpgradeModalOpen(false);
    setUpgradeTrigger(null);
  }, []);

  // Check if feature requires upgrade and trigger modal if needed
  const checkFeatureOrUpgrade = useCallback(async (feature, workspaceId = null) => {
    const wsId = workspaceId || currentWorkspace?.id;
    if (!wsId) return false;

    try {
      const features = await getWorkspaceFeatures(wsId);
      if (!features) return false;

      const featureEnabled = features[feature];
      if (!featureEnabled) {
        // Feature not available - open upgrade modal
        openUpgradeModal(feature);
        return false;
      }
      return true;
    } catch (err) {
      console.error('Feature check failed:', err);
      return false;
    }
  }, [currentWorkspace, getWorkspaceFeatures, openUpgradeModal]);

  // Check if can create film (respects film limit)
  const canCreateFilmOrUpgrade = useCallback(async (workspaceId = null) => {
    const wsId = workspaceId || currentWorkspace?.id;
    if (!wsId) return false;

    try {
      const usage = await getWorkspaceUsage(wsId);
      if (!usage) return false;

      if (!usage.films.can_create) {
        openUpgradeModal('second_film');
        return false;
      }
      return true;
    } catch (err) {
      console.error('Film limit check failed:', err);
      return false;
    }
  }, [currentWorkspace, getWorkspaceUsage, openUpgradeModal]);

  // Check if workspace is on free plan
  const isFreePlan = useCallback((workspace = null) => {
    const ws = workspace || currentWorkspace;
    return ws?.plan_id === 'plan_free';
  }, [currentWorkspace]);

  // Check if workspace is on pro plan (tier-aware: matches plan_pro_v2 and
  // the retired legacy plan_pro id)
  const isProPlan = useCallback((workspace = null) => {
    const ws = workspace || currentWorkspace;
    return ['plan_pro', 'plan_pro_v2'].includes(ws?.plan_id);
  }, [currentWorkspace]);

  const value = useMemo(() => ({
    workspaces,
    currentWorkspace,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    loading,
    error,
    selectWorkspace,
    canCreateWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    loadWorkspaces,
    getWorkspaceFeatures,
    getWorkspaceUsage,
    getWorkspaceUpgradeStatus,
    hasMultipleWorkspaces: workspaces.length > 1,
    hasSingleWorkspace: workspaces.length === 1,
    hasNoWorkspace: workspaces.length === 0,
    // Upgrade functionality
    upgradeModalOpen,
    upgradeTrigger,
    openUpgradeModal,
    closeUpgradeModal,
    checkFeatureOrUpgrade,
    canCreateFilmOrUpgrade,
    isFreePlan,
    isProPlan,
  }), [
    workspaces,
    currentWorkspace,
    currentWorkspaceId,
    setCurrentWorkspaceId,
    loading,
    error,
    selectWorkspace,
    canCreateWorkspace,
    createWorkspace,
    updateWorkspace,
    deleteWorkspace,
    loadWorkspaces,
    getWorkspaceFeatures,
    getWorkspaceUsage,
    getWorkspaceUpgradeStatus,
    upgradeModalOpen,
    upgradeTrigger,
    openUpgradeModal,
    closeUpgradeModal,
    checkFeatureOrUpgrade,
    canCreateFilmOrUpgrade,
    isFreePlan,
    isProPlan,
  ]);

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export default WorkspaceContext;
